<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{ isEditing ? "Edit Content Pack" : "Add New Content Pack" }}
    </h2>
    <Card>
      <template #header>
        <div class="p-3 pb-0 flex flex-column gap-3">
          <div
            class="flex flex-column sm:flex-row justify-content-between gap-3"
          >
            <InputText
              class="w-full sm:w-20rem"
              v-model="contentPackName"
              type="text"
              placeholder="Content Pack Name"
            />
            <Button
              class="justify-content-center"
              @click="saveContentPack"
              :loading="loading"
            >
              {{ isEditing ? "Update" : "Create" }} Content Pack
            </Button>
          </div>

          <!-- Search and Filters Section -->
          <div class="flex flex-column sm:flex-row gap-3">
            <span class="p-input-icon-left flex-grow-1">
              <i class="pi pi-search" />
              <InputText
                v-model="globalSearchValue"
                class="w-full"
                placeholder="Search shows..."
              />
            </span>
            <MultiSelect
              v-model="selectedCategories"
              :options="availableCategories"
              placeholder="Filter Categories"
              class="w-full sm:w-15rem"
            />
            <MultiSelect
              v-model="selectedHosted"
              :options="availableHosted"
              placeholder="Filter Hosted"
              class="w-full sm:w-15rem"
            />
          </div>

          <div class="flex gap-2">
            <Button
              type="button"
              label="Add All Filtered"
              @click="addAllFiltered"
              size="small"
              outlined
            />
            <Button
              type="button"
              label="Remove All"
              @click="removeAllShows"
              size="small"
              outlined
            />
          </div>
        </div>
      </template>

      <template #content>
        <!-- Selected Shows Table -->
        <div class="mb-4">
          <div class="flex justify-content-between align-items-center mb-2">
            <h3 class="m-0">Shows in Content Pack</h3>
            <div class="flex gap-2">
              <Button
                icon="pi pi-upload"
                label="Import from CSV"
                class="p-button-sm"
                severity="success"
                @click="triggerFileInput"
              />
              <input
                ref="csvFileInput"
                type="file"
                accept=".csv"
                style="display: none"
                @change="handleCSVImport"
              />
              <Button
                icon="pi pi-download"
                label="Export to CSV"
                class="p-button-sm"
                @click="exportToCSV"
                :disabled="selectedShowsList.length === 0"
              />
            </div>
          </div>
          <DataTable
            :value="selectedShowsList"
            :paginator="true"
            :rows="20"
            class="p-datatable-sm"
            :striped-rows="true"
            :loading="loading"
            sortField="show_title"
            :sortOrder="1"
          >
            <Column>
              <template #body="slotProps">
                <Button
                  icon="pi pi-minus"
                  class="p-button-rounded p-button-danger p-button-sm"
                  @click="removeShow(slotProps.data)"
                />
              </template>
            </Column>
            <Column
              field="show_id"
              header="ID"
              :sortable="true"
              style="max-width: 100px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.show_id }}</div>
              </template>
            </Column>
            <Column
              field="show_title"
              header="Title"
              :sortable="true"
              style="max-width: 300px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.show_title }}</div>
              </template>
            </Column>
            <Column
              field="hosted"
              header="Hosted"
              :sortable="true"
              style="max-width: 150px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.hosted }}</div>
              </template>
            </Column>
            <Column
              field="categories"
              header="Categories"
              :sortable="true"
              style="max-width: 200px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.categories }}</div>
              </template>
            </Column>
            <Column
              field="rss_url"
              header="RSS Link"
              :sortable="true"
              style="max-width: 200px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.rss_url }}</div>
              </template>
            </Column>
            <Column header="Included In Content Packs" :sortable="false">
              <template #body="slotProps">
                <div class="flex flex-wrap gap-1">
                  <Tag
                    v-for="pack in showContentPacks[slotProps.data.show_id]"
                    :key="pack.id"
                    :value="pack.name"
                    severity="info"
                    class="text-xs"
                  />
                  <span
                    v-if="!showContentPacks[slotProps.data.show_id]?.length"
                  >
                    None
                  </span>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Available Shows Table -->
        <div>
          <h3>Available Shows</h3>
          <DataTable
            :value="availableShowsList"
            :paginator="true"
            :rows="20"
            class="p-datatable-sm"
            :striped-rows="true"
            :loading="loading"
            sortField="show_title"
            :sortOrder="1"
          >
            <Column>
              <template #body="slotProps">
                <Button
                  icon="pi pi-plus"
                  class="p-button-rounded p-button-success p-button-sm"
                  @click="addShow(slotProps.data)"
                />
              </template>
            </Column>
            <Column
              field="show_id"
              header="ID"
              :sortable="true"
              style="max-width: 100px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.show_id }}</div>
              </template>
            </Column>
            <Column
              field="show_title"
              header="Title"
              :sortable="true"
              style="max-width: 300px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.show_title }}</div>
              </template>
            </Column>
            <Column
              field="hosted"
              header="Hosted"
              :sortable="true"
              style="max-width: 150px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.hosted }}</div>
              </template>
            </Column>
            <Column
              field="categories"
              header="Categories"
              :sortable="true"
              style="max-width: 200px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.categories }}</div>
              </template>
            </Column>
            <Column
              field="rss_url"
              header="RSS Link"
              :sortable="true"
              style="max-width: 200px"
            >
              <template #body="slotProps">
                <div class="truncate">{{ slotProps.data.rss_url }}</div>
              </template>
            </Column>
            <Column header="Included In Content Packs" :sortable="false">
              <template #body="slotProps">
                <div class="flex flex-wrap gap-1">
                  <Tag
                    v-for="pack in showContentPacks[slotProps.data.show_id]"
                    :key="pack.id"
                    :value="pack.name"
                    severity="info"
                    class="text-xs"
                  />
                  <span
                    v-if="!showContentPacks[slotProps.data.show_id]?.length"
                  >
                    None
                  </span>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <ConfirmDialog></ConfirmDialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import { API_BASE_URL } from "../consts.js";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import Card from "primevue/card";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import MultiSelect from "primevue/multiselect";
import Tag from "primevue/tag";
import ConfirmDialog from "primevue/confirmdialog";

const toast = useToast();
const confirm = useConfirm();
const store = useStore();
const router = useRouter();
const route = useRoute();
const contentPackName = ref("");
const selectedShows = ref([]);
const loading = ref(false);
const selectedCategories = ref([]);
const selectedHosted = ref([]);
const globalSearchValue = ref("");
const contentPacks = ref([]);
const showContentPacks = ref({});
const selectedContentPackFilters = ref([]);
const shows = ref([]);
const csvFileInput = ref(null);

const token = localStorage.getItem("adminToken");
const packId = computed(() => route.params.id);
const isEditing = computed(() => !!packId.value);

// Add computed property for content pack filter options
const contentPackFilterOptions = computed(() => {
  const options = [
    { id: "none", name: "None" }, // Special option for shows not in any pack
  ];

  // Add all content packs as options
  contentPacks.value.forEach((pack) => {
    options.push({
      id: pack.id,
      name: pack.name,
    });
  });

  return options;
});

// Existing computed properties
const availableCategories = computed(() => {
  const categoriesSet = new Set();
  shows.value.forEach((show) => {
    if (show.categories) {
      show.categories.split(",").forEach((category) => {
        categoriesSet.add(category.trim());
      });
    }
  });
  return Array.from(categoriesSet).sort();
});

const availableHosted = computed(() => {
  return [
    ...new Set(shows.value.map((show) => show.hosted).filter(Boolean)),
  ].sort();
});

// Modified filteredShows to include content pack filtering
const filteredShows = computed(() => {
  let filtered = [...shows.value];

  // Apply global search
  if (globalSearchValue.value) {
    const searchTerm = globalSearchValue.value.toLowerCase();
    filtered = filtered.filter((show) => {
      return (
        show.show_title?.toLowerCase().includes(searchTerm) ||
        show.hosted?.toLowerCase().includes(searchTerm) ||
        show.categories?.toLowerCase().includes(searchTerm) ||
        show.rss_url?.toLowerCase().includes(searchTerm)
      );
    });
  }

  // Apply content pack filter
  if (selectedContentPackFilters.value.length > 0) {
    filtered = filtered.filter((show) => {
      const showPacks = showContentPacks.value[show.show_id] || [];

      return selectedContentPackFilters.value.some((filterPack) => {
        if (filterPack.id === "none") {
          // Show matches if it's not in any content pack
          return showPacks.length === 0;
        } else {
          // Show matches if it's in this content pack
          return showPacks.some((pack) => pack.id === filterPack.id);
        }
      });
    });
  }

  // Apply category filter
  if (selectedCategories.value.length > 0) {
    filtered = filtered.filter((show) => {
      const showCats = show.categories?.split(",").map((c) => c.trim()) || [];
      return selectedCategories.value.some((cat) => showCats.includes(cat));
    });
  }

  // Apply hosted filter
  if (selectedHosted.value.length > 0) {
    filtered = filtered.filter((show) =>
      selectedHosted.value.includes(show.hosted)
    );
  }

  return filtered;
});

const fetchAllContentPacks = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/content-packs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    contentPacks.value = response.data;

    // Create mapping of show IDs to content packs
    const mapping = {};
    contentPacks.value.forEach((pack) => {
      pack.shows.forEach((showId) => {
        if (!mapping[showId]) {
          mapping[showId] = [];
        }
        mapping[showId].push({
          id: pack.id,
          name: pack.name,
        });
      });
    });
    showContentPacks.value = mapping;
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch content packs",
      life: 3000,
    });
  }
};

// Rest of the existing functions remain the same
const selectAllFiltered = () => {
  selectedShows.value = [...filteredShows.value];
};

const clearSelection = () => {
  selectedShows.value = [];
};

const fetchContentPack = async () => {
  if (!isEditing.value) return;

  try {
    loading.value = true;
    const response = await axios.get(
      `${API_BASE_URL}/content-packs/${packId.value}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    contentPackName.value = response.data.name;
    selectedShows.value = shows.value.filter((show) =>
      response.data.shows.includes(show.show_id)
    );
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch content pack details",
      life: 3000,
    });
    router.push("/admin/content-packs");
  } finally {
    loading.value = false;
  }
};

// Add these new methods
const addShow = (show) => {
  selectedShows.value.push(show);
};

const removeShow = (show) => {
  selectedShows.value = selectedShows.value.filter(
    (s) => s.show_id !== show.show_id
  );
};

// Add these new computed properties
const selectedShowsList = computed(() => {
  return selectedShows.value;
});

const availableShowsList = computed(() => {
  const selected = new Set(selectedShows.value.map((show) => show.show_id));
  return filteredShows.value.filter((show) => !selected.has(show.show_id));
});

// Update saveContentPack to use selectedShows directly
const saveContentPack = async () => {
  if (!contentPackName.value) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Please enter a content pack name",
      life: 3000,
    });
    return;
  }

  try {
    loading.value = true;
    const payload = {
      name: contentPackName.value,
      shows: selectedShows.value.map((show) => show.show_id),
    };

    const url = isEditing.value
      ? `${API_BASE_URL}/content-packs/${packId.value}`
      : `${API_BASE_URL}/content-packs`;

    const method = isEditing.value ? "put" : "post";

    await axios[method](url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    toast.add({
      severity: "success",
      summary: "Success",
      detail: `Content pack ${
        isEditing.value ? "updated" : "created"
      } successfully`,
      life: 3000,
    });

    router.push("/admin/content-packs");
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: `Failed to ${isEditing.value ? "update" : "create"} content pack`,
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
};

const removeAllShows = () => {
  selectedShows.value = [];
};

const addAllFiltered = () => {
  availableShowsList.value.forEach((show) => {
    if (!selectedShows.value.find((s) => s.show_id === show.show_id)) {
      selectedShows.value.push(show);
    }
  });
};

// CSV Import functionality
const triggerFileInput = () => {
  csvFileInput.value.click();
};

const parseCSVLine = (line) => {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++; // skip next quote
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
};

const processCSVImport = (file) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter((line) => line.trim());

      if (lines.length < 2) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "CSV file is empty or has no data rows",
          life: 3000,
        });
        return;
      }

      // Parse header to find Show ID column
      const headers = parseCSVLine(lines[0]);
      const showIdIndex = headers.findIndex(
        (h) => h.toLowerCase().replace(/['"]/g, "").trim() === "show id"
      );

      if (showIdIndex === -1) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail:
            'CSV must have a "Show ID" column. Expected format: Show ID, Title, Hosted, Categories, RSS URL',
          life: 5000,
        });
        return;
      }

      // Extract show IDs from CSV
      const csvShowIds = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        if (cols[showIdIndex]) {
          csvShowIds.push(cols[showIdIndex].replace(/['"]/g, "").trim());
        }
      }

      if (csvShowIds.length === 0) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "No show IDs found in CSV",
          life: 3000,
        });
        return;
      }

      // Match CSV show IDs against loaded shows
      const showIdSet = new Set(csvShowIds);
      const matchedShows = shows.value.filter((show) =>
        showIdSet.has(show.show_id)
      );
      const unmatchedCount = csvShowIds.length - matchedShows.length;

      // Apply the import
      selectedShows.value = matchedShows;

      toast.add({
        severity: "success",
        summary: "Import Successful",
        detail: `Imported ${matchedShows.length} shows from CSV`,
        life: 3000,
      });

      if (unmatchedCount > 0) {
        toast.add({
          severity: "warn",
          summary: "Warning",
          detail: `${unmatchedCount} show(s) from CSV were not found in the system`,
          life: 5000,
        });
      }
    } catch (error) {
      console.error("CSV parse error:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to parse CSV file. Please check the file format.",
        life: 3000,
      });
    }
  };
  reader.readAsText(file);
};

const handleCSVImport = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Reset file input so the same file can be selected again
  event.target.value = "";

  // If editing an existing pack and it already has shows, show warning
  if (isEditing.value && selectedShows.value.length > 0) {
    confirm.require({
      message:
        "Importing a CSV will replace ALL current shows in this content pack. This action cannot be undone. Are you sure you want to continue?",
      header: "Import Warning",
      icon: "pi pi-exclamation-triangle",
      acceptClass: "p-button-danger",
      accept: () => processCSVImport(file),
    });
  } else {
    processCSVImport(file);
  }
};

const exportToCSV = () => {
  if (selectedShowsList.value.length === 0) {
    toast.add({
      severity: "info",
      summary: "Info",
      detail: "No shows to export",
      life: 3000,
    });
    return;
  }

  // Convert data to CSV format
  const headers = ["Show ID", "Title", "Hosted", "Categories", "RSS URL"];
  let csvContent = headers.join(",") + "\n";

  selectedShowsList.value.forEach((show) => {
    const row = [
      `"${show.show_id || ""}"`,
      `"${(show.show_title || "").replace(/"/g, '""')}"`,
      `"${(show.hosted || "").replace(/"/g, '""')}"`,
      `"${(show.categories || "").replace(/"/g, '""')}"`,
      `"${(show.rss_url || "").replace(/"/g, '""')}"`,
    ];
    csvContent += row.join(",") + "\n";
  });

  // Create a blob and download link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${contentPackName.value || "content-pack"}-shows.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast.add({
    severity: "success",
    summary: "Success",
    detail: "CSV file exported successfully",
    life: 3000,
  });
};

onMounted(async () => {
  await store.dispatch("loadShows", { token });
  shows.value = store.state.shows || [];
  await fetchAllContentPacks();
  if (isEditing.value) {
    await fetchContentPack();
  }
});
</script>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
